import { ReactElement } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { Label } from 'components/v2/Label'
import { Select, StyledSelect } from 'components/v2/Select'
import { RoutingProjectPlanAutocomplete } from 'features/RoutingProjects/components/PlanAutocomplete'
import { IRoutingProjectModel, IRoutingProjectPlan } from 'features/RoutingProjects/types'
import { RoutingProjectPlanStatus } from 'gql/generated/graphql'
import { safeFormatMessage } from 'utils/intl'
import { InputContent } from 'components/Input'

const Grid = styled.div`
  display: grid;
  grid-template-columns: 140px 1fr;
  gap: 0px;

  ${StyledSelect} {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  ${InputContent} {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: none;
  }
`

const statusMessages = defineMessages({
  [RoutingProjectPlanStatus.Simulation]: {
    id: 'routingProject.selectPlan.status.simulation',
    defaultMessage: 'Simulation'
  }
})

const STATUS_LIST = [RoutingProjectPlanStatus.Simulation]

interface ISelectRoutingProjecPlanProps {
  routingProjectModel: IRoutingProjectModel
  selectedPlan: IRoutingProjectPlan | null
  setSelectedPlan: (plan: IRoutingProjectPlan | null) => void
  planStatus: RoutingProjectPlanStatus
  setPlanStatus: (status: RoutingProjectPlanStatus) => void
}

export const SelectRoutingProjecPlan = ({
  routingProjectModel,
  selectedPlan,
  setSelectedPlan,
  planStatus,
  setPlanStatus
}: ISelectRoutingProjecPlanProps): ReactElement => {
  const intl = useIntl()

  const handleStatusChange = ({ target: { value } }: React.ChangeEvent<HTMLSelectElement>): void => {
    setPlanStatus(value as RoutingProjectPlanStatus)
  }
  const handlePlanChange = (plan: IRoutingProjectPlan | null): void => {
    setSelectedPlan(plan)
  }

  return (
    <div>
      <Label>
        <FormattedMessage id="routingProject.selectPlan.title" defaultMessage="Plan" />
      </Label>
      <Grid>
        <Select value={planStatus} onChange={handleStatusChange} primary>
          {STATUS_LIST.map((status) => (
            <option key={status} value={status}>
              {safeFormatMessage({ intl, messages: statusMessages, key: status })}
            </option>
          ))}
        </Select>
        <RoutingProjectPlanAutocomplete
          plans={routingProjectModel.plans.filter((plan) => plan.status === planStatus)}
          onChange={(plan): void => handlePlanChange(plan)}
          value={selectedPlan?.name || ''}
        />
      </Grid>
    </div>
  )
}
